import {Button, Checkbox, FormControlLabel, Stack, TextField, Typography} from '@mui/material';
import React, {Fragment} from 'react';
import {FormProvider} from 'react-hook-form';

import {AlertInfo, MaskedInput} from 'presentation/components';

import {OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {masks} from 'presentation/utils';
import {Loading} from 'presentation/components/Loading/Loading';
import {useMediaQueries} from 'presentation/pages';
import {DiscardChangesModal} from '../../DiscardChangesModal';
import {FieldsContainer, StyledTextField} from './styles';
import {AddressFormProps} from './types';
import {FavoriteAddressField} from './FavoriteAddressField';
import {useAddressForm} from '../hooks/useAddressForm';

const {cancel, save} = translations['pt-br'].commons;
const {
	address: addressLabel,
	addressErrors,
	successAlert,
	errorAlert,
	alertInfo,
} = translations['pt-br'].privateFlow.profile.changeData;

const warningText = {
	apiError: addressErrors.apiError,
	allFieldsEmpty: addressErrors.allFieldsEmpty,
};

function AddressForm({
	onCancel,
	onSave,
	loading,
	address,
	withoutAlertInfo,
	withoutCancelButton,
	responsiveButtons,
	hasFavoriteAddressField,
	favoriteAddressDescription,
	saveLabel = save,
}: AddressFormProps) {
	const {isMobile} = useMediaQueries();

	const {
		methods,
		showOtherFields,
		operationState,
		fieldDisabledState,
		warningInfo,
		unmaskedZipCode,
		watchWithoutNumber,
		showAlert,
		typeMessage,
		isNewProfileFeatureFlagEnabled,
		isDiscardChangesModalOpenState,
		setIsDiscardChangesModalOpenState,
		handleBack,
		handleCheckbox,
		handleChange,
		handleSubmit,
		register,
		errors,
	} = useAddressForm({
		onCancel,
		address,
		hasFavoriteAddressField,
		favoriteAddressDescription,
	});

	const message = {
		success: successAlert.address,
		error: errorAlert,
	};

	return (
		<Fragment>
			{isNewProfileFeatureFlagEnabled && showAlert && !warningInfo ? (
				<AlertInfo.Root severity={typeMessage}>
					<AlertInfo.Title sx={{mb: 0}} title={message[typeMessage]} />
				</AlertInfo.Root>
			) : null}
			{isNewProfileFeatureFlagEnabled && !withoutAlertInfo ? (
				<AlertInfo.Root>
					<AlertInfo.Description>{alertInfo}</AlertInfo.Description>
				</AlertInfo.Root>
			) : null}
			{warningInfo ? (
				<AlertInfo.Root severity="warning" sx={{mb: 2}}>
					<AlertInfo.Description>{warningText[warningInfo]}</AlertInfo.Description>
				</AlertInfo.Root>
			) : null}
			<FormProvider {...methods}>
				<Stack gap={2}>
					<MaskedInput
						{...register('zipCode')}
						id="zipCode"
						inputMode="numeric"
						label={addressLabel.zipCode}
						placeholder={addressLabel.zipCode}
						error={!!errors.zipCode}
						helperText={
							errors.zipCode?.message
								? errors.zipCode.message
								: addressLabel.zipCodeInfo
						}
						slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
						maskOptions={masks.zipCode}
						$fieldName="zipCode"
						onChange={e => handleChange(e.target.value)}
						endAdornmentVariant={
							operationState === OperationStateTypesEnum.PENDING
								? 'loading'
								: 'tooltip'
						}
						tooltipText={addressLabel.tooltip}
					/>
					{showOtherFields ? (
						<Stack gap={2}>
							<TextField
								{...register('address')}
								id="typeAddress"
								error={!!errors.address}
								helperText={
									errors.address?.message
										? errors.address.message
										: addressLabel.addressInfo
								}
								slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
								fullWidth
								label={addressLabel.address}
								placeholder={addressLabel.address}
								disabled={
									unmaskedZipCode.length === 8 && fieldDisabledState.address
								}
							/>

							<FieldsContainer>
								<StyledTextField
									{...register('number')}
									id="number"
									sx={{flex: 1}}
									disabled={watchWithoutNumber}
									error={!!errors.number && !watchWithoutNumber}
									label={addressLabel.number}
									placeholder={addressLabel.number}
									helperText={
										errors.number?.message && !watchWithoutNumber
											? errors.number.message
											: ''
									}
									slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
									inputMode="numeric"
									type="number"
								/>
								<FormControlLabel
									sx={{
										width: 'fit-content',
										height:
											errors.number?.message && !watchWithoutNumber
												? 'fit-content'
												: 'auto',
									}}
									control={
										<Checkbox
											{...register('withoutNumber')}
											onChange={handleCheckbox}
											checked={watchWithoutNumber}
										/>
									}
									label={
										<Typography noWrap>{addressLabel.withoutNumber}</Typography>
									}
									labelPlacement="end"
								/>
							</FieldsContainer>

							{isNewProfileFeatureFlagEnabled ? (
								<Fragment>
									<TextField
										{...register('complement')}
										id="complement"
										error={!!errors.complement}
										helperText={
											errors.complement?.message
												? errors.complement.message
												: addressLabel.complementInfo
										}
										slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
										fullWidth
										label={`${addressLabel.complement} ${addressLabel.optional}`}
										placeholder={addressLabel.complement}
									/>
									<TextField
										{...register('district')}
										id="district"
										error={!!errors.district}
										helperText={
											errors.district?.message
												? errors.district.message
												: undefined
										}
										slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
										fullWidth
										label={addressLabel.district}
										placeholder={addressLabel.district}
										disabled={
											unmaskedZipCode.length === 8 &&
											fieldDisabledState.district
										}
									/>
								</Fragment>
							) : (
								<FieldsContainer>
									<TextField
										{...register('complement')}
										id="complement"
										error={!!errors.complement}
										helperText={
											errors.complement?.message
												? errors.complement.message
												: addressLabel.complementInfo
										}
										slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
										fullWidth
										label={`${addressLabel.complement} ${addressLabel.optional}`}
										placeholder={addressLabel.complement}
									/>
									<TextField
										{...register('district')}
										id="district"
										error={!!errors.district}
										helperText={
											errors.district?.message
												? errors.district.message
												: undefined
										}
										slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
										fullWidth
										label={addressLabel.district}
										placeholder={addressLabel.district}
										disabled={
											unmaskedZipCode.length === 8 &&
											fieldDisabledState.district
										}
									/>
								</FieldsContainer>
							)}

							<FieldsContainer>
								<TextField
									{...register('city')}
									id="city"
									error={!!errors.city}
									helperText={
										errors.city?.message ? errors.city.message : undefined
									}
									slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
									fullWidth
									label={addressLabel.city}
									placeholder={addressLabel.city}
									disabled={
										unmaskedZipCode.length === 8 && fieldDisabledState.city
									}
								/>
								<TextField
									{...register('state')}
									id="state"
									error={!!errors.state}
									helperText={
										errors.state?.message ? errors.state.message : undefined
									}
									slotProps={{
										formHelperText: {sx: {fontSize: '0.75rem'}},
										htmlInput: {maxLength: 2},
									}}
									fullWidth
									label={addressLabel.state}
									placeholder={addressLabel.state}
									disabled={
										unmaskedZipCode.length === 8 && fieldDisabledState.state
									}
								/>
							</FieldsContainer>
							{hasFavoriteAddressField ? <FavoriteAddressField /> : null}
						</Stack>
					) : null}
					<Stack
						direction={responsiveButtons && isMobile ? 'column' : 'row'}
						flex={1}
						justifyContent={responsiveButtons && isMobile ? 'center' : 'flex-end'}
						spacing={2}
						mt={responsiveButtons && isMobile ? 2 : undefined}>
						{!withoutCancelButton && (
							<Button
								disabled={loading}
								size="large"
								variant="outlined"
								onClick={handleBack}>
								{cancel}
							</Button>
						)}
						{showOtherFields ? (
							<Button disabled={loading} size="large" onClick={handleSubmit(onSave)}>
								<Stack gap={1} direction="row">
									{saveLabel}
									{loading ? <Loading size={18} color="inherit" /> : null}
								</Stack>
							</Button>
						) : null}
					</Stack>
				</Stack>
				<DiscardChangesModal
					open={isDiscardChangesModalOpenState}
					setOpen={setIsDiscardChangesModalOpenState}
					onConfirm={onCancel}
				/>
			</FormProvider>
		</Fragment>
	);
}

export {AddressForm};
